/**
 * Shared helpers for promotion-stack rendering.
 *
 * Used by:
 *   - src/components/business/PromotionStackPanel.js
 *   - src/components/PromotionsAnalytics.js
 *   - src/components/business/EventsManager.js
 */

/**
 * Convert anything timestamp-like to a JS millisecond epoch.
 * Accepts Firestore Timestamp, raw {seconds,nanoseconds}, Date, ISO string,
 * or numeric epoch.
 */
export function tsToMs(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (ts.seconds) return ts.seconds * 1000 + (ts.nanoseconds || 0) / 1e6;
  if (ts._seconds) return ts._seconds * 1000;
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts === "string") {
    const d = new Date(ts).getTime();
    return Number.isFinite(d) ? d : 0;
  }
  const n = Number(ts);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Partition promotion stacks into {active, scheduled, past}.
 * Cancelled stacks always go to past.
 */
export function groupStacks(stacks, nowMs = Date.now()) {
  const active = [];
  const scheduled = [];
  const past = [];
  for (const s of stacks || []) {
    const startMs = tsToMs(s.startAt);
    const endMs = tsToMs(s.endAt);
    if (s.status === "cancelled") {
      past.push(s);
    } else if (endMs && endMs <= nowMs) {
      past.push(s);
    } else if (startMs > nowMs) {
      scheduled.push(s);
    } else {
      active.push(s);
    }
  }
  // Sort active by soonest-to-expire, scheduled by soonest-to-start, past by most-recent end.
  active.sort((a, b) => tsToMs(a.endAt) - tsToMs(b.endAt));
  scheduled.sort((a, b) => tsToMs(a.startAt) - tsToMs(b.startAt));
  past.sort((a, b) => tsToMs(b.endAt) - tsToMs(a.endAt));
  return { active, scheduled, past };
}

/**
 * Human countdown like "3d 4h", "5h 12m", "in 2m", "ending soon".
 * Pass a positive delta (target - now) in milliseconds.
 */
export function formatCountdown(deltaMs) {
  if (!Number.isFinite(deltaMs)) return "";
  if (deltaMs <= 0) return "ending now";
  const sec = Math.floor(deltaMs / 1000);
  if (sec < 60) return "<1m";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  if (hr < 24) return remMin ? `${hr}h ${remMin}m` : `${hr}h`;
  const day = Math.floor(hr / 24);
  const remHr = hr % 24;
  if (day < 30) return remHr ? `${day}d ${remHr}h` : `${day}d`;
  const mon = Math.floor(day / 30);
  return `${mon}mo`;
}
